/**
 * Vikunja import, browser half.
 *
 * The api reads the instance (Vikunja sends no CORS headers, so the browser
 * cannot) and hands back raw tasks. The writing happens HERE, as a normal Yjs
 * client, the same way the note importers seed docs: seq assignment, the
 * projection, stats and the Discord outbox all follow from a normal doc edit.
 *
 * The API token lives in this store only until the fetch returns, and is not
 * persisted anywhere.
 */
import { create } from "zustand";
import {
  applyTaskImport,
  mapVikunjaTask,
  type TaskImportEntry,
  type TaskImportOutcome,
  type VikunjaFetchResult,
  type VikunjaRawTask,
} from "@hydra-notes/shared";
import { ctxFor, getData } from "@/data/store";
import { canEditIn } from "@/commands/nav";
import { useAuth } from "@/data/auth";

/**
 * Tasks per transaction. Small on purpose: each chunk is one synchronous Yjs
 * transaction plus the observer and persistence work it wakes, so a big chunk
 * freezes the whole app mid-import and the sidebar stops responding to clicks.
 * Fifty keeps every pause short enough that the UI stays usable.
 */
const CHUNK = 50;

/** Give the browser a real chance to paint between chunks, not just a tick. */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}

export interface ImportTarget {
  /** Existing list, or "" together with newListTitle to create one. */
  listId: string;
  spaceId: string;
  newListTitle?: string;
  /** Display name of the destination, for the running screen. */
  listName?: string;
}

export interface ImportOptions {
  projectIds: number[];
  footer: boolean;
}

export interface ImportSummary extends TaskImportOutcome {
  listId: string;
  spaceId: string;
  notes: string[];
}

export interface ImportProgress {
  written: number;
  total: number;
  /** Where the tasks are going, so the running screen can say so. */
  listName: string;
}

interface VikunjaState {
  phase: "idle" | "connecting" | "ready" | "importing" | "done";
  payload: VikunjaFetchResult | null;
  error: string | null;
  progress: ImportProgress;
  summary: ImportSummary | null;
  connect: (baseUrl: string, token: string, includeDone: boolean) => Promise<boolean>;
  runImport: (target: ImportTarget, options: ImportOptions) => Promise<boolean>;
  reset: () => void;
}

export const useVikunjaImport = create<VikunjaState>((set, get) => ({
  phase: "idle",
  payload: null,
  error: null,
  progress: { written: 0, total: 0, listName: "" },
  summary: null,

  reset: () =>
    set({ phase: "idle", payload: null, error: null, progress: { written: 0, total: 0, listName: "" }, summary: null }),

  connect: async (baseUrl, token, includeDone) => {
    set({ phase: "connecting", error: null });
    try {
      const res = await fetch("/api/vikunja/fetch", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ baseUrl, token, includeDone }),
      });
      const body = (await res.json().catch(() => null)) as (VikunjaFetchResult & { error?: string }) | null;
      if (!res.ok || !body || !("tasks" in body)) {
        set({ phase: "idle", error: body?.error ?? "Could not reach that Vikunja instance." });
        return false;
      }
      set({ phase: "ready", payload: body, error: null });
      return true;
    } catch {
      set({ phase: "idle", error: "Could not reach that Vikunja instance." });
      return false;
    }
  },

  runImport: async (target, options) => {
    const payload = get().payload;
    if (!payload) return false;

    const ctx = target.spaceId ? ctxFor(target.spaceId) : getData();
    if (!ctx) {
      set({ error: "That space is not loaded any more. Reopen it and try again." });
      return false;
    }
    if (!canEditIn(ctx)) {
      set({ error: "You have view-only access to that space." });
      return false;
    }

    const userId = useAuth.getState().user?.id ?? "local";
    const wanted = new Set(options.projectIds);
    const projectTitles = new Map(payload.projects.map((p) => [p.id, p.title]));
    const selected: VikunjaRawTask[] = payload.tasks.filter(
      (t) => typeof t.project_id !== "number" || wanted.has(t.project_id),
    );

    const entries: TaskImportEntry[] = selected.map((raw) =>
      mapVikunjaTask(raw, {
        host: payload.instance.host,
        baseUrl: payload.instance.url,
        ...(typeof raw.project_id === "number" && projectTitles.has(raw.project_id)
          ? { projectTitle: projectTitles.get(raw.project_id) }
          : {}),
        footer: options.footer,
      }),
    );

    const listName = target.listName?.trim() || target.newListTitle?.trim() || "your list";
    set({ phase: "importing", error: null, progress: { written: 0, total: entries.length, listName } });

    // create the destination list first, so a failure here happens before any
    // task is written
    let listId = target.listId;
    if (!listId) {
      listId = ctx.workspace.createItem("tasklist", null, target.newListTitle?.trim() || "Vikunja");
    }

    const handle = ctx.tasks.open(listId);
    const totals: TaskImportOutcome = { created: 0, updated: 0, skipped: 0 };
    try {
      await handle.whenLoaded;
      for (let i = 0; i < entries.length; i += CHUNK) {
        const outcome = applyTaskImport(handle.doc, entries.slice(i, i + CHUNK), userId);
        totals.created += outcome.created;
        totals.updated += outcome.updated;
        totals.skipped += outcome.skipped;
        set({ progress: { written: Math.min(i + CHUNK, entries.length), total: entries.length, listName } });
        await yieldToBrowser();
      }
      ctx.workspace.touch(listId);
    } finally {
      handle.release();
    }

    const notes: string[] = [];
    if (payload.truncated) {
      notes.push(`Your instance has more than ${payload.cap} tasks, so the import stopped at that many.`);
    }
    const unmapped = selected.filter(
      (t) =>
        t.done !== true &&
        ((t.repeat_after ?? 0) > 0 || t.repeat_mode === 1) &&
        !entries.find((e) => e.sourceId.endsWith(`:${t.id}`))?.recurrence,
    ).length;
    if (unmapped > 0) {
      notes.push(`${unmapped} repeating task${unmapped === 1 ? "" : "s"} had a schedule that does not map here, noted in the description.`);
    }

    set({
      phase: "done",
      summary: { ...totals, listId, spaceId: target.spaceId, notes },
      progress: { written: entries.length, total: entries.length, listName },
    });
    return true;
  },
}));
